import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { SocketContext } from '../../context/SocketContext'
import { useHideMenu } from '../../hooks/useHideMenu'
import { useGetPantallas } from '../../hooks/useGetPantallas';
import { getUsuarioStorage } from '../../helpers/getUsuarioStorage'

import { NoAcceso } from '../../components/NoAcceso';
import LoadingOverlay from '../../components/LoadingOverlay'
import { SelectBuscable } from '../../components/SelectBuscable'

import { UsuariosRoles } from './components/UsuariosRoles';
import { RolesPantallas } from './components/RolesPantallas';
import { serverApi } from '../../api';
import { CrearUsuarioModal } from './components/CrearUsuarioModal';
import { EditarUsuarioModal } from './components/EditarUsuarioModal';
import { CambiarPasswordModal } from './components/CambiarPasswordModal';


const MySwal = withReactContent(Swal);

const sweet = (title, html, icon) => { MySwal.fire({ title, html, icon, confirmButtonColor: 'var(--color-primario)', timer: 15000 }) }
const sweetConfirm = (title, html, icon) => {
    return MySwal.fire({
        title,
        html,
        icon,
        confirmButtonText: 'Ok',
        confirmButtonColor: 'var(--color-primario)',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        cancelButtonText: 'Cancelar'
    })
}

const paginacionBtn = {
    width: 34, height: 34, borderRadius: 10, border: '1.5px solid #e2e8f0',
    background: '#ffffff', color: 'var(--color-primario-texto)', cursor: 'pointer', fontSize: 12,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
}

const botonAccion = (bg, color) => ({
    width: 30, height: 30, borderRadius: 9, border: 'none',
    background: bg, color, cursor: 'pointer', fontSize: 12,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background .15s',
})

const chipStyle = {
    background: '#f1f5f9', color: '#475569', fontWeight: 700,
    padding: '5px 11px', borderRadius: 999, fontSize: 12,
    display: 'inline-flex', alignItems: 'center', gap: 5,
    whiteSpace: 'nowrap', lineHeight: 1.4,
};

const chipOk = { background: '#d1fae5', color: '#047857' };
const chipNo = { background: '#fee2e2', color: '#b91c1c' };

const tabBtn = (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '10px 16px', borderRadius: 12, cursor: 'pointer', fontSize: 13.5, fontWeight: 800,
    border: active ? 'none' : '1.5px solid #e2e8f0',
    background: active ? 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-secundario) 100%)' : '#ffffff',
    color: active ? 'white' : '#475569',
    boxShadow: active ? '0 4px 14px rgba(var(--color-primario-rgb),0.3)' : '0 1px 3px rgba(0,0,0,0.04)',
    transition: 'all .15s',
});

const TABS = [
    { key: 'usuarios', label: 'Usuarios', icon: 'fa-users' },
    { key: 'usuariosRoles', label: 'Asignación roles a usuarios', icon: 'fa-user-tag' },
    { key: 'rolesPantallas', label: 'Roles / Pantallas', icon: 'fa-table-columns' },
];

const iniciales = (nombre) => {
    return (nombre || '?')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0))
        .join('')
        .toUpperCase();
};


export const UsuariosPage = () => {

    useHideMenu(false);
    const { socket } = useContext(SocketContext);
    const usuario = useMemo(() => getUsuarioStorage(), []);
    const pantallas = useGetPantallas();

    const [showModal, setShowModal] = useState(false);
    const [showEditar, setShowEditar] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [tab, setTab] = useState('usuarios');

    const [query, setQuery] = useState({
        search: '',
        page: 1,
        limit: 10,
        sortBy: 'nombreCompleto',
        sortDir: 'asc',
    });

    // tabla
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const totalPages = Math.max(1, Math.ceil(total / query.limit));

    const [searchInput, setSearchInput] = useState('');


    const navigate = useNavigate();
    useEffect(() => {
        if (usuario.uidUsuario === null) {
            navigate("/Ingreso")
        }
    }, [usuario.uidUsuario, navigate])


    const loadUsuarios = useCallback(async () => {

        const codigoEntidad = usuario.codigoEntidad;
        if (!codigoEntidad) return;

        try {

            setIsLoading(true);

            const resp = await serverApi.post(
                `/usuarios/lista-usuarios`,
                {
                    ...query,
                    codigoEntidad,
                    codigoUbicacion: usuario.codigoUbicacion,
                    itemsPerPage: query.limit,
                    sortedBy: query.sortBy,
                    sortDirection: query.sortDir,
                }
            );

            setData(resp.data.lista || []);
            setTotal(resp.data.total || 0);

        } catch (error) {
            console.error("Error al obtener usuarios", error);
        } finally {
            setIsLoading(false);
        }

    }, [query, usuario]);

    useEffect(() => {
        loadUsuarios();
    }, [loadUsuarios]);


    useEffect(() => {
        if (!socket) return;

        const handler = (lista) => {
            setData(Array.isArray(lista?.lista) ? lista.lista : []);
            setTotal(lista?.total || 0);
        };

        socket.on('lista-usuarios', handler);

        return () => {
            socket.off('lista-usuarios', handler);
        }
    }, [socket])

    useEffect(() => {
        const timeout = setTimeout(() => {

            if (searchInput.trim().length > 0 && searchInput.trim().length < 2) return;

            setQuery(prev => {
                if (prev.search === searchInput) return prev; // 🔥 evita re-render

                return {
                    ...prev,
                    search: searchInput,
                    page: 1
                };
            });

        }, 800);

        return () => clearTimeout(timeout);

    }, [searchInput]);


    const paginate = (page) => {
        if (page < 1 || page > totalPages) return;

        setQuery(prev => ({
            ...prev,
            page
        }));
    };

    const handleSortChange = (e) => {
        const [sortBy, sortDir] = e.target.value.split(':');
        setQuery(prev => ({ ...prev, sortBy, sortDir, page: 1 }));
    };

    // Parámetros extra para que el socket devuelva la misma página/orden que se está viendo
    const paramsVista = {
        ...query,
        codigoUbicacion: usuario.codigoUbicacion,
        itemsPerPage: query.limit,
        sortedBy: query.sortBy,
        sortDirection: query.sortDir,
    };

    const inactivaUsuario = async (uidUsuario, accion) => {

        const codigoEntidad = usuario.codigoEntidad;
        const codigoUbicacion = usuario.codigoUbicacion;
        if (!codigoEntidad || codigoEntidad === null || !codigoUbicacion || codigoUbicacion === null) return sweet('Atención!', 'Revise la infromación', 'error');


        const act = accion === 1 ? "Activar" : "Inactivar";

        const confirmacion = await sweetConfirm("Atención", `Esta seguro de ${act} el registro?`, "warning").then((result) => {
            return (result);
        })

        if (!confirmacion.value) return false;

        try {
            setIsLoading(true);

            await serverApi.post(
                `/usuarios/inactiva-usuario`,
                {
                    ...paramsVista,
                    uidUsuario,
                    accion,
                    codigoEntidad,
                    codigoUbicacion,
                }
            );

            loadUsuarios();

        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }

    }

    const registraUsuario = async (info) => {


        const codigoEntidad = usuario.codigoEntidad;
        if (!codigoEntidad || codigoEntidad === null) return sweet('Atención!', 'Revise la infromación', 'error');

        try {

            setIsLoading(true);
            const res = await serverApi.post(
                `/usuarios/registra-usuario`,
                {
                    ...info,
                    ...paramsVista,
                    codigoEntidad,
                }
            );

            if (res.data.ok === false) {
                sweet('', res.data.resp, 'error');
            } else {
                sweet('', 'Usuario creado', 'success');
                setShowModal(false);
                loadUsuarios();
            }

        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);

        }

    }

    // Actualiza un campo del usuario (usuario / nombreCompleto / password)
    const editaUsuario = async (campo, valor, uidUsuario, valorAnterior) => {

        const codigoEntidad = usuario.codigoEntidad;
        const codigoUbicacion = usuario.codigoUbicacion;
        if (!codigoEntidad || codigoEntidad === null) return sweet('Atención!', 'Revise la infromación', 'error');

        if (campo === 'password') {
            const confirmacion = await sweetConfirm("Atención", "Esta seguro de cambiar el password?", "warning");
            if (!confirmacion.value) return false;
        }

        const esCampoVisible = campo === 'usuario' || campo === 'nombreCompleto';

        if (esCampoVisible) {
            setData(prev => prev.map(item =>
                item.uidUsuario === uidUsuario ? { ...item, [campo]: valor } : item
            ));
        }

        try {
            setIsLoading(true);

            const res = await serverApi.post(
                `/usuarios/edita-usuario`,
                {
                    ...paramsVista,
                    campo,
                    valor,
                    uidUsuario,
                    codigoEntidad,
                    codigoUbicacion,
                }
            );

            if (res.data.ok === false) {
                if (esCampoVisible) {
                    setData(prev => prev.map(item =>
                        item.uidUsuario === uidUsuario ? { ...item, [campo]: valorAnterior } : item
                    ));
                }
                sweet('', res.data.resp, 'error');
                return false;
            }

            return true;

        } catch (error) {
            console.error(error);

            if (esCampoVisible) {
                setData(prev => prev.map(item =>
                    item.uidUsuario === uidUsuario ? { ...item, [campo]: valorAnterior } : item
                ));
            }
            return false;

        } finally {
            setIsLoading(false);

        }

    }

    const abrirEditar = (item) => {
        setUsuarioSeleccionado(item);
        setShowEditar(true);
    };

    const abrirPassword = (item) => {
        setUsuarioSeleccionado(item);
        setShowPassword(true);
    };

    const guardarEdicionUsuario = async (datos) => {

        const item = usuarioSeleccionado;
        if (!item) return;

        const confirmacion = await sweetConfirm("Atención", "Esta seguro de modificar el usuario?", "warning");
        if (!confirmacion.value) return false;

        const nuevoUsuario = datos.usuario.toUpperCase();
        const nuevoNombre = datos.nombreCompleto.toUpperCase();
        const usuarioAnterior = (item.usuario || '').toUpperCase();
        const nombreAnterior = (item.nombreCompleto || '').toUpperCase();

        if (nuevoUsuario === usuarioAnterior && nuevoNombre === nombreAnterior) {
            setShowEditar(false);
            return;
        }

        let ok = true;

        // Optimista
        setData(prev => prev.map(it =>
            it.uidUsuario === item.uidUsuario
                ? { ...it, usuario: nuevoUsuario, nombreCompleto: nuevoNombre }
                : it
        ));

        if (nuevoUsuario !== usuarioAnterior) {
            ok = await editaUsuario('usuario', nuevoUsuario, item.uidUsuario, item.usuario);
        }

        if (ok && nuevoNombre !== nombreAnterior) {
            ok = await editaUsuario('nombreCompleto', nuevoNombre, item.uidUsuario, item.nombreCompleto);
        }

        if (ok) {
            sweet('', 'Usuario actualizado', 'success');
            setShowEditar(false);
            loadUsuarios();
        } else {
            loadUsuarios();
        }
    };

    const guardarPassword = async (datos) => {

        const item = usuarioSeleccionado;
        if (!item) return;

        const ok = await editaUsuario('password', datos.password.toUpperCase(), item.uidUsuario);

        if (ok) {
            sweet('', 'Contraseña actualizada', 'success');
            setShowPassword(false);
        }
    };


    if (usuario.uidUsuario === null) {
        return (<></>)
    }
    else if (!pantallas.includes("usuarios")) {
        return (<NoAcceso />)
    } else {

        return (
            <>
                {isLoading && <LoadingOverlay />}

                <div className={`content ${isLoading ? 'opacity' : ''}`} style={{ padding: '0 24px 24px', fontFamily: "'Inter', Arial, sans-serif" }}>

                    <div style={{ maxWidth: 1320, margin: '0 auto' }}>

                        {/* Tabs */}
                        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                            {TABS.map((t) => (
                                <button
                                    key={t.key}
                                    style={tabBtn(tab === t.key)}
                                    onClick={() => setTab(t.key)}
                                >
                                    <i className={`fa-solid ${t.icon}`}></i>
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        {tab === 'usuarios' && (
                            <div style={{
                                background: 'white', borderRadius: 16, overflow: 'hidden',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0',
                            }}>

                                {/* Header de la card */}
                                <div style={{
                                    padding: '20px 26px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
                                    display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'space-between', alignItems: 'center',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                        <div style={{
                                            width: 46, height: 46, borderRadius: 14,
                                            background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-secundario) 100%)',
                                            color: 'var(--color-marca-contraste)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 20, boxShadow: '0 4px 14px rgba(var(--color-primario-rgb),0.3)',
                                        }}>
                                            <i className="fa-solid fa-users"></i>
                                        </div>
                                        <div>
                                            <h4 style={{ margin: 0, fontWeight: 800, fontSize: 18, color: '#0f172a' }}>Usuarios</h4>
                                            <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#64748b' }}>
                                                Administra los usuarios de la entidad
                                            </p>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                        {usuario.nombreEntidad && (
                                            <span style={{
                                                background: '#ecfdf5', color: '#047857',
                                                fontWeight: 800, padding: '6px 14px', borderRadius: 12, fontSize: 13,
                                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                                maxWidth: '100%', overflow: 'hidden',
                                            }}>
                                                <i className="fa-solid fa-building" style={{ flexShrink: 0 }}></i>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {usuario.nombreEntidad.toUpperCase()}
                                                </span>
                                            </span>
                                        )}

                                        <button
                                            onClick={() => setShowModal(true)}
                                            style={{
                                                padding: '11px 20px', borderRadius: 12, border: 'none',
                                                background: 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-secundario) 100%)',
                                                color: 'var(--color-marca-contraste)', fontWeight: 800, cursor: 'pointer', fontSize: 13.5,
                                                boxShadow: '0 4px 14px rgba(var(--color-primario-rgb),0.3)',
                                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                            }}
                                        >
                                            <i className="fa-solid fa-plus"></i> Agregar usuario
                                        </button>
                                    </div>
                                </div>

                                {/* Barra de herramientas */}
                                <div style={{
                                    padding: '16px 26px', borderBottom: '1px solid #f1f5f9',
                                    display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end', justifyContent: 'space-between',
                                }}>
                                    <div style={{ position: 'relative', flex: 1, maxWidth: 420, minWidth: 220 }}>
                                        <i className="fa-solid fa-magnifying-glass" style={{
                                            position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)',
                                            color: '#94a3b8', fontSize: 13,
                                        }} />
                                        <input
                                            type='text'
                                            placeholder='Buscar por usuario o nombre...'
                                            maxLength={40}
                                            value={searchInput}
                                            onChange={(ev) => setSearchInput(ev.target.value)}
                                            style={{
                                                width: '100%', padding: '10px 12px 10px 38px', borderRadius: 10,
                                                border: '1.5px solid #e2e8f0', fontSize: 13.5, outline: 'none',
                                                background: '#f8fafc', transition: 'border-color .15s, background .15s',
                                            }}
                                            onFocus={(e) => { e.target.style.borderColor = 'var(--color-primario)'; e.target.style.background = '#ffffff'; }}
                                            onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                                        />
                                    </div>

                                    <div style={{ width: 230, maxWidth: '100%' }}>
                                        <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>
                                            Ordenar por
                                        </label>
                                        <SelectBuscable
                                            name="sort"
                                            asEvent
                                            onChange={handleSortChange}
                                            isClearable={false}
                                            options={[
                                                { value: 'nombreCompleto:asc', label: 'Nombre (A–Z)' },
                                                { value: 'nombreCompleto:desc', label: 'Nombre (Z–A)' },
                                                { value: 'usuario:asc', label: 'Usuario (A–Z)' },
                                                { value: 'usuario:desc', label: 'Usuario (Z–A)' },
                                            ]}
                                            value={`${query.sortBy}:${query.sortDir}`}
                                            fontSize="13.5px"
                                        />
                                    </div>
                                </div>

                                {/* Lista de usuarios */}
                                <div style={{ padding: '18px 26px', background: '#f8fafc' }}>
                                    {data.length === 0 ? (
                                        <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                                            <div style={{ color: '#94a3b8', fontSize: 34, marginBottom: 8 }}>
                                                <i className="fa-solid fa-users"></i>
                                            </div>
                                            <div style={{ fontWeight: 700, color: '#64748b', fontSize: 15 }}>
                                                No hay usuarios registrados
                                            </div>
                                            <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>
                                                Agrega el primer usuario de la entidad.
                                            </div>
                                        </div>
                                    ) : (
                                        data.map((item) => {
                                            const activo = item.indActivo === 1;
                                            return (
                                                <div
                                                    key={item.uidUsuario}
                                                    style={{
                                                        background: '#ffffff', borderRadius: 14,
                                                        border: `1.5px solid ${activo ? '#e2e8f0' : '#fecaca'}`,
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '16px 18px', marginBottom: 12,
                                                        display: 'flex', flexWrap: 'wrap', gap: 14,
                                                        alignItems: 'center', justifyContent: 'space-between',
                                                        opacity: activo ? 1 : 0.72,
                                                        transition: 'box-shadow .15s, border-color .15s',
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(var(--color-primario-rgb),0.12)'; e.currentTarget.style.borderColor = 'var(--color-primario)'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; e.currentTarget.style.borderColor = activo ? '#e2e8f0' : '#fecaca'; }}
                                                >
                                                    {/* Avatar + nombre */}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 220, flex: '1 1 260px' }}>
                                                        <div style={{
                                                            width: 46, height: 46, borderRadius: 12, flexShrink: 0,
                                                            background: activo ? 'linear-gradient(135deg, var(--color-primario) 0%, var(--color-secundario) 100%)' : '#cbd5e1',
                                                            color: 'var(--color-marca-contraste)', fontWeight: 800, fontSize: 15,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}>
                                                            {iniciales(item.nombreCompleto)}
                                                        </div>
                                                        <div style={{ minWidth: 0 }}>
                                                            <div style={{ fontWeight: 800, color: '#0f172a', fontSize: 14.5, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                                {(item.nombreCompleto || '').toUpperCase()}
                                                                {!activo && (
                                                                    <span className="badge" style={{ background: '#fee2e2', color: '#b91c1c', fontWeight: 800, padding: '4px 10px', borderRadius: 999, fontSize: 11 }}>
                                                                        INACTIVO
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div style={{ color: '#64748b', fontSize: 12.5, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <i className="fa-solid fa-at" style={{ fontSize: 11 }}></i>
                                                                {(item.usuario || '').toUpperCase()}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Chips de información */}
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', flex: '1 1 400px' }}>
                                                        <span style={chipStyle}>
                                                            <i className="fa-solid fa-user"></i> Usuario: <b style={{ color: '#0f172a' }}>{(item.usuario || '').toUpperCase()}</b>
                                                        </span>
                                                        <span style={activo ? { ...chipStyle, ...chipOk } : { ...chipStyle, ...chipNo }}>
                                                            {activo ? <i className="fa-solid fa-check"></i> : <i className="fa-solid fa-ban"></i>}
                                                            Estado: {activo ? 'Activo' : 'Inactivo'}
                                                        </span>
                                                        {item.createdAt && (
                                                            <span style={chipStyle}>
                                                                <i className="fa-solid fa-calendar"></i> {String(item.createdAt).slice(0, 10)}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Acciones */}
                                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                        <button
                                                            title="Editar"
                                                            style={botonAccion('var(--color-secundario-suave)', 'var(--color-primario)')}
                                                            onClick={() => abrirEditar(item)}
                                                        >
                                                            <i className="fa-solid fa-pen"></i>
                                                        </button>
                                                        <button
                                                            title="Cambiar contraseña"
                                                            style={botonAccion('#fef3c7', '#92400e')}
                                                            onClick={() => abrirPassword(item)}
                                                        >
                                                            <i className="fa-solid fa-key"></i>
                                                        </button>
                                                        {activo ? (
                                                            <button
                                                                title="Inactivar"
                                                                style={botonAccion('#fee2e2', '#b91c1c')}
                                                                onClick={() => inactivaUsuario(item.uidUsuario, 0)}
                                                            >
                                                                <i className="fa-solid fa-ban"></i>
                                                            </button>
                                                        ) : (
                                                            <button
                                                                title="Activar"
                                                                style={botonAccion('#d1fae5', '#047857')}
                                                                onClick={() => inactivaUsuario(item.uidUsuario, 1)}
                                                            >
                                                                <i className="fa-solid fa-check"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Paginación */}
                                <div style={{
                                    padding: '14px 26px', borderTop: '1px solid #f1f5f9',
                                    background: '#f8fafc', display: 'flex', flexWrap: 'wrap', gap: 10,
                                    justifyContent: 'space-between', alignItems: 'center',
                                }}>
                                    <span style={{ color: '#64748b', fontSize: 13 }}>
                                        Registros: <b style={{ color: '#0f172a' }}>{total}</b>
                                    </span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ fontSize: 13, color: '#475569' }}>
                                            Página {query.page} de {totalPages}
                                        </span>
                                        <button
                                            onClick={() => paginate(query.page - 1)}
                                            disabled={query.page === 1}
                                            style={{ ...paginacionBtn, opacity: query.page === 1 ? 0.4 : 1 }}
                                        >
                                            <i className="fa-solid fa-chevron-left"></i>
                                        </button>
                                        <button
                                            onClick={() => paginate(query.page + 1)}
                                            disabled={query.page >= totalPages}
                                            style={{ ...paginacionBtn, opacity: query.page >= totalPages ? 0.4 : 1 }}
                                        >
                                            <i className="fa-solid fa-chevron-right"></i>
                                        </button>
                                    </div>
                                </div>

                            </div>
                        )}

                        {tab === 'usuariosRoles' && <UsuariosRoles />}

                        {tab === 'rolesPantallas' && <RolesPantallas />}

                    </div>
                </div>

                <CrearUsuarioModal
                    show={showModal}
                    onClose={() => setShowModal(false)}
                    onSave={(datos) => registraUsuario(datos)}
                />

                <EditarUsuarioModal
                    show={showEditar}
                    onClose={() => setShowEditar(false)}
                    onSave={guardarEdicionUsuario}
                    initialData={usuarioSeleccionado}
                />

                <CambiarPasswordModal
                    show={showPassword}
                    onClose={() => setShowPassword(false)}
                    onSave={guardarPassword}
                    usuario={usuarioSeleccionado}
                />
            </>
        )
    }
}
